<template>
  <v-row>
    <v-col cols="12" style="position: relative;">
      <v-card>
        <v-btn
          absolute
          dark
          fab
          top
          right
          small
          color="error"
          @click="cancel"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>

        <div v-if="loading" class="text-center pa-6">
          <v-progress-circular
            :size="40"
            :width="7"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>

        <v-card-text v-else>
          <v-form v-model="form.valid" ref="form" :disabled="disabled" lazy-validation>
            <v-text-field
              label="First Name"
              v-model="form.value.first_name"
              :rules="form.rules.first_name"
              outlined
            />

            <v-text-field
              label="Last Name"
              v-model="form.value.last_name"
              :rules="form.rules.last_name"
              outlined
            />

            <v-text-field
              label="Email"
              v-model="form.value.email"
              :rules="form.rules.email"
              outlined
            />

            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="Phone"
                  v-model="form.value.mobile_phone"
                  v-mask="masks.mobile_phone"
                  :rules="form.rules.mobile_phone"
                  outlined
                />
              </v-col>

              <v-col cols="6">
                <v-select
                  label="Role"
                  v-model="form.value.role"
                  :rules="form.rules.role"
                  :items="catalogs.adminRoles"
                  outlined
                />
              </v-col>
            </v-row>

            <v-checkbox
              v-model="form.value.is_active"
              label="Active"
            ></v-checkbox>
          </v-form>

          <v-divider v-if="adminId"></v-divider>

          <v-form lazy-validation >
            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="New Password"
                  v-model="passwords.value.password"
                  type="password"
                  outlined
                  :error-messages="passwords.errors"
                  @input="checkPasswordForm"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Confirm New Password"
                  v-model="passwords.value.confirm_password"
                  type="password"
                  outlined
                  :error-messages="passwords.errors"
                  @input="checkPasswordForm"
                />
              </v-col>
            </v-row>
          </v-form>

          <v-btn v-if="adminId" color="error" class="mr-4" v-text="'Delete'" @click="remove"></v-btn>
          <v-btn
            color="primary"
            class="mr-4"
            :disabled="!form.valid || !passwords.valid"
            v-text="'Save changes'"
            @click="save"
          ></v-btn>
          <v-btn color="primary" outlined v-text="'Cancel'" @click="cancel"></v-btn>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { PHONE_MASK, EMAIL_PATTERN } from '@/global/consts.js';
import {
  getAdminById,
  updateAdmin,
  createAdmin,
  deleteAdmin,
} from './api';

export const ADMIN_ROLES = [
  { text: 'System Admin', value: 'systemAdmin' },
  { text: 'Employee', value: 'employee' },
];

export default {
  name: 'EditAdmin',
  data: () => ({
    disabled: false,
    loading: false,
    adminId: null,
    form: {
      valid: true,
      value: {
        first_name: '',
        last_name: '',
        email: '',
        mobile_phone: '',
        role: '',
        is_active: false,
      },
      rules: {
        first_name: [v => !!v || 'First name is required'],
        last_name: [v => !!v || 'Last name is required'],
        email: [v => !!v || 'Email is required!', v => EMAIL_PATTERN.test(v) || 'Invalid e-mail.'],
        mobile_phone: [v => !!v || 'Phone is required!'],
        role: [v => !!v || 'Role is required'],
      },
    },
    passwords: {
      valid: true,
      dirty: false,
      value: {
        password: '',
        confirm_password: '',
      },
      errors: [],
    },
    masks: {
      mobile_phone: PHONE_MASK,
    },
    catalogs: {
      adminRoles: ADMIN_ROLES,
    },
  }),
  computed: {
    emptyPasswords() {
      const { password, confirm_password } = this.passwords.value;
      return password.length === 0 && confirm_password.length === 0;
    },
    passwordsMatch() {
      const { password, confirm_password } = this.passwords.value;
      return password === confirm_password;
    },
  },
  mounted () {
    this.adminId = this.$route.params.admin_id
      ? parseInt(this.$route.params.admin_id, 10)
      : null;

    if (this.adminId) {
      this.get();
    }
  },
  methods: {
    checkPasswordForm() {
      this.passwords.dirty = true;

      if (this.emptyPasswords || this.passwordsMatch) {
        this.passwords.valid = true;
        this.passwords.errors = [];
      } else {
        this.passwords.valid = false;
        this.passwords.errors.push('Passwords don\'t match');
      }
    },
    save() {
      const adminFormValid = this.$refs.form.validate();
      const passwordFormValid = this.passwords.valid;

      if (!adminFormValid || !passwordFormValid) {
        return;
      }

      if (this.adminId) {
        this.update();
      } else {
        this.create();
      }
    },
    get() {
      this.loading = true;

      getAdminById(this.adminId)
        .then(admin => this.setForm(admin))
        .finally(() => (this.loading = false));
    },
    update() {
      this.loading = true;

      const { value, dirty } = this.passwords;

      updateAdmin(this.adminId, {
        ...this.form.value,
        password: dirty ? value.password : null,
      })
        .then(() => this.cancel())
        .finally(() => (this.loading = false));
    },
    create() {
      if (!this.validatePasswordForm()) {
        return;
      }

      this.loading = true;

      createAdmin({ ...this.form.value, ...this.passwords.value })
        .then(() => this.cancel())
        .finally(() => (this.loading = false));
    },
    remove() {
      const text = {
        title: `Delete admin`,
        text: `You are about to delete admin «${this.form.value.first_name} ${this.form.value.last_name}». Are you sure to proceed?`,
      };

      this.loading = true;
      this.$swal.fire(text.title, text.text)
        .then(({ isConfirmed }) => isConfirmed
          ? deleteAdmin(this.adminId).then(() => this.cancel())
          : Promise.resolve()
        );
    },
    cancel() {
      this.$router.push({ name: 'system-admin-admins' })
    },
    setForm(admin) {
      this.form.value = {
        first_name: admin.first_name,
        last_name: admin.last_name,
        email: admin.email,
        mobile_phone: admin.mobile_phone,
        role: admin.role,
        is_active: admin.is_active
      };
    },
    validatePasswordForm() {
      let valid = false;

      if (!this.passwords.dirty) {
        this.passwords.errors.push('Insert correct password');
      } else if (this.passwords.dirty && this.emptyPasswords) {
        this.passwords.errors.push('Please insert passwords');
      } else {
        valid = true;
      }

      return valid;
    },
  }
}
</script>

<style>

</style>